import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Chip,
  InputBase,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import FunctionsIcon from "@mui/icons-material/Functions";
import SearchIcon from "@mui/icons-material/Search";
// import { FORMULA_CATEGORIES, getFunctionSyntax, getAllFunctionsWithCategory } from "../../utils/formulaFunctions";
import { BASIC_FORMULAS, getFunctionSyntax, getAllBasicFormulas } from "../../utils/formulaFunctions";
import {
  prepareTextForInsertion,
  validateBasicFormulaSyntax,
} from "../../utils/formulaValidation";
import Dropdown from "../Dropdown/Dropdown";
import { cleanTableName } from "../../utils/tableNameHelper";

const AddColumnModal = ({
  open,
  onClose,
  onAddColumn,
  onUpdateColumn,
  onDeleteColumn,
  editMode = false,
  editColumnName = null,
  editFormula = null,
  isPostgres = false,
  defaultTableName = null,
  existingComputedColumns = [],
  sqlError = null
}) => {
  const [columnName, setColumnName] = useState("");
  const [formula, setFormula] = useState("");
  const [error, setError] = useState("");
  const [showFunctions, setShowFunctions] = useState(false);
  const [functionSearch, setFunctionSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableColumns, setTableColumns] = useState([]);
  const [tableOptions, setTableOptions] = useState([]);
  const tableDataMapRef = React.useRef({}); // Store table data by name
  const formulaInputRef = React.useRef(null); // textarea element for formula
  const cursorPositionRef = React.useRef(0); // last known cursor position in formula

  // Load columns from selected table
  const loadTableColumns = useCallback((tableData) => {
    if (tableData && tableData.children && Array.isArray(tableData.children)) {
      const columns = tableData.children
        .filter((col) => col.type === "column" && col.name)
        .map((col) => ({
          name: col.name,
          // Preserve the original datatype coming from schema
          textDatatype: col.textDatatype || "",
          type: col.textDatatype || "",
          tableName: col.tableName || tableData.name || "",
        }));
      setTableColumns(columns);
    } else {
      setTableColumns([]);
    }
  }, []);

  // Load tables and columns from localStorage
  useEffect(() => {
    if (open) {
      try {
        const storageKeys = isPostgres
          ? ["postgres_tables_data", "postgres_shared_tables_data"]
          : ["snowflake_tables_data"];

        const tableDataMap = {};
        const allTables = [];

        storageKeys.forEach((key) => {
          const tablesDataStr = localStorage.getItem(key);
          if (!tablesDataStr) return;

          try {
            const tablesData = JSON.parse(tablesDataStr);
            if (Array.isArray(tablesData)) {
              tablesData.forEach((database) => {
                if (database?.children && Array.isArray(database.children)) {
                  database.children.forEach((table) => {
                    if (
                      table &&
                      table.type === "Table" &&
                      table.name &&
                      !tableDataMap[table.name]
                    ) {
                      const cleanName = cleanTableName(table.name);
                      allTables.push({
                        value: table.name,
                        label: cleanName,
                      });
                      tableDataMap[table.name] = table;
                    }
                  });
                }
              });
            }
          } catch (err) {
            console.error(`Error parsing table data for key ${key}:`, err);
          }
        });

        tableDataMapRef.current = tableDataMap;
        setTableOptions(allTables);

        if (defaultTableName && tableDataMap[defaultTableName]) {
          if (selectedTable !== defaultTableName) {
            setSelectedTable(defaultTableName);
          }
          loadTableColumns(tableDataMap[defaultTableName]);
        } else if (selectedTable && tableDataMap[selectedTable]) {
          loadTableColumns(tableDataMap[selectedTable]);
        } else {
          setTableColumns([]);
        }
      } catch (error) {
        console.error("Error loading tables from localStorage:", error);
        tableDataMapRef.current = {};
        setTableOptions([]);
        setTableColumns([]);
      }
    }
  }, [open, isPostgres, selectedTable, loadTableColumns, defaultTableName]);

  // Handle table selection
  const handleTableChange = useCallback((tableName) => {
    setSelectedTable(tableName);
    const tableData = tableDataMapRef.current[tableName];
    if (tableData) {
      loadTableColumns(tableData);
    } else {
      setTableColumns([]);
    }
  }, [loadTableColumns]);

  // Determine which columns to display
  // Only show columns from selected table
  const displayColumns = selectedTable && tableColumns.length > 0 ? tableColumns : [];

  useEffect(() => {
    if (open) {
      if (editMode && editColumnName && editFormula) {
        setColumnName(editColumnName);
        setFormula(editFormula);
      } else {
        // Reset for add mode
        setColumnName("");
        setFormula("");
      }
      // Set error from sqlError prop if present
      if (sqlError) {
        setError(sqlError);
      } else {
        setError("");
      }
      setShowFunctions(false);
      setFunctionSearch("");
      setSelectedCategory(0);
      // Note: Table selection is handled in the tables loading useEffect above
      // Don't reset selectedTable here to avoid conflicts
    }
  }, [open, editMode, editColumnName, editFormula, sqlError]);

  // Track cursor position whenever user interacts with the textarea
  const handleCursorPositionChange = useCallback(() => {
    const el = formulaInputRef.current;
    if (el && typeof el.selectionStart === "number") {
      cursorPositionRef.current = el.selectionStart;
    }
  }, []);

  const handleAdd = () => {
    const trimmedName = columnName.trim();
    const trimmedFormula = formula.trim();

    // Validate inputs
    if (!trimmedName) {
      setError("Column name is required");
      return;
    }

    // Validate column name - only allow letters, numbers, underscores, and spaces
    const columnNameRegex = /^[a-zA-Z0-9_ ]+$/;
    if (!columnNameRegex.test(trimmedName)) {
      setError("Column name can only contain letters, numbers, underscores, and spaces. Special characters are not allowed.");
      return;
    }

    // Column name should start with a letter or underscore
    const startsWithValidChar = /^[a-zA-Z_]/;
    if (!startsWithValidChar.test(trimmedName)) {
      setError("Column name must start with a letter or underscore");
      return;
    }

    if (!trimmedFormula) {
      setError("Formula is required");
      return;
    }

    // In edit mode, prevent no-op updates (same name + same formula)
    // Note: allow updating formula with same name, and allow renaming with same formula.
    if (
      editMode &&
      editColumnName &&
      editFormula &&
      trimmedName === String(editColumnName).trim() &&
      trimmedFormula === String(editFormula).trim()
    ) {
      setError("No changes to update");
      return;
    }

    // Check if column name already exists (skip check if editing the same column)
    // Use tableColumns if a table is selected, otherwise skip validation
    const columnsToCheck = selectedTable && tableColumns.length > 0 ? tableColumns : [];
    
    if (editMode) {
      // In edit mode, only check if the new name conflicts with other columns (not the one being edited)
      const otherColumns = columnsToCheck.filter(
        (col) => {
          const colName = typeof col === "string" ? col : col.name;
          return colName !== editColumnName;
        }
      );
      const columnNames = otherColumns.map((col) => typeof col === "string" ? col : col.name);
      if (columnNames.includes(trimmedName)) {
        setError(`Column "${trimmedName}" already exists in the table`);
        return;
      }
      
      // Check against other existing computed columns (excluding the one being edited)
      const otherComputedColumns = (existingComputedColumns || []).filter(
        (cc) => cc.columnName !== editColumnName
      );
      const computedColumnNames = otherComputedColumns.map((cc) => cc.columnName);
      if (computedColumnNames.includes(trimmedName)) {
        setError(`A calculated column named "${trimmedName}" already exists`);
        return;
      }
    } else {
      // In add mode, check against all columns from selected table
      if (columnsToCheck.length > 0) {
        const columnNames = columnsToCheck.map((col) => typeof col === "string" ? col : col.name);
        if (columnNames.includes(trimmedName)) {
          setError(`Column "${trimmedName}" already exists in the table`);
          return;
        }
      }
      
      // Check against existing computed columns
      const computedColumnNames = (existingComputedColumns || []).map((cc) => cc.columnName);
      if (computedColumnNames.includes(trimmedName)) {
        setError(`A calculated column named "${trimmedName}" already exists`);
        return;
      }
    }

    // Validate formula using basic frontend validation
    const formulaValidationResult = validateBasicFormulaSyntax(trimmedFormula);
    if (!formulaValidationResult.isValid) {
      setError(formulaValidationResult.errorMessage);
      return;
    }

    // Call the appropriate handler
    // Get tableName from selected table or from the first column's tableName
    const tableName =
      selectedTable ||
      (tableColumns.length > 0 ? tableColumns[0].tableName : "");

    // Infer datatype from the formula by looking at referenced columns.
    // We build a map of sanitized columnName (spaces -> underscores) -> type,
    // then find the first column actually used in the formula.
    let inferredDataType = null;
    if (tableColumns.length > 0) {
      const columnTypeMap = {};
      tableColumns.forEach((col) => {
        const colName =
          typeof col === "string" ? col : col?.name || "";
        // Always prefer the original textDatatype if available
        const colType =
          typeof col === "object"
            ? col?.textDatatype || col?.type || null
            : null;
        if (!colName) return;
        const sanitized = colName.replace(/\s+/g, "_");
        if (sanitized) {
          columnTypeMap[sanitized] = colType;
        }
      });

      const rawFormula = formula.trim().replace(/^=/, "");
      if (rawFormula) {
        const colKeys = Object.keys(columnTypeMap);
        for (const key of colKeys) {
          const re = new RegExp(`\\b${key}\\b`, "i");
          if (re.test(rawFormula) && columnTypeMap[key]) {
            inferredDataType = columnTypeMap[key];
            break;
          }
        }
      }

      // Fallback: if we couldn't match anything in the formula,
      // use the first table column's type as a best-effort default.
      if (!inferredDataType) {
        const first = tableColumns[0];
        if (first) {
          inferredDataType = first.textDatatype || first.type || null;
        }
      }
    }
    
    if (editMode && onUpdateColumn) {
      onUpdateColumn(editColumnName, trimmedName, trimmedFormula);
    } else {
      // Pass tableName and inferredDataType
      onAddColumn(
        trimmedName,
        trimmedFormula,
        tableName,
        inferredDataType
      );
    }
    onClose();
  };

  const insertIntoFormula = useCallback((textToInsert) => {
    const formulaInputElement = formulaInputRef.current;

    setFormula((previousFormula) => {
     const rawCursor =
        formulaInputElement && typeof formulaInputElement.selectionStart === "number"
          ? formulaInputElement.selectionStart
          : cursorPositionRef.current;
      const cursorPosition = Math.min(Math.max(0, rawCursor ?? 0), previousFormula.length);

      // Work with the original formula (not trimmed) for cursor position accuracy
      let currentFormula = previousFormula;
      let insertPosition = cursorPosition;

      // Ensure formula starts with "="
      if (!currentFormula || currentFormula.length === 0) {
        currentFormula = "=";
        insertPosition = 1; // Position after "="
      } else if (!currentFormula.startsWith("=")) {
        currentFormula = "=" + currentFormula;
        insertPosition = cursorPosition + 1; // Adjust for added "="
      }

      // Use validation utility to determine if comma is needed
      const textWithCommaIfNeeded = prepareTextForInsertion(
        currentFormula,
        insertPosition,
        textToInsert
      );

      const updatedFormula =
        currentFormula.slice(0, insertPosition) +
        textWithCommaIfNeeded +
        currentFormula.slice(insertPosition);

      // Restore cursor position after state update
      setTimeout(() => {
        if (formulaInputElement) {
          formulaInputElement.focus();
          const newCursorPosition = insertPosition + textWithCommaIfNeeded.length;
          cursorPositionRef.current = newCursorPosition;
          formulaInputElement.setSelectionRange(newCursorPosition, newCursorPosition);
        }
      }, 0);

      return updatedFormula;
    });
    setError("");
  }, []);

  const handleInsertColumn = useCallback((col) => {
    // Handle both string (backward compatibility) and object format
    if (!col) return; // Guard against null/undefined
    
    const colName = typeof col === "string" ? col : (col.name || "");
    if (!colName) return; // Guard against empty name
    
    const sanitizedColName = colName.replace(/\s+/g, "_");
    if (sanitizedColName) {
      insertIntoFormula(sanitizedColName);
    }
  }, [insertIntoFormula]); // Include insertIntoFormula in dependencies

  const handleInsertFunction = (funcName) => {
    const syntax = getFunctionSyntax(funcName);
    // Extract function name and insert with parentheses
    const funcCall = syntax.includes("(") 
      ? syntax.substring(0, syntax.indexOf("(") + 1) 
      : `${funcName}(`;
    insertIntoFormula(funcCall);
    setShowFunctions(false);
  };

  // Filter functions based on search
  const getFilteredFunctions = () => {
    const allFunctions = getAllBasicFormulas();
    if (!functionSearch.trim()) {
      return allFunctions;
    }
    const searchLower = functionSearch.toLowerCase();
    return allFunctions.filter(
      (func) =>
        func.toLowerCase().includes(searchLower) ||
        getFunctionSyntax(func).toLowerCase().includes(searchLower)
    );
  };

  const categoryNames = Object.keys(BASIC_FORMULAS);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          overflow: "hidden",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header - Fixed */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #3F49A7 0%, #2c357a 100%)",
          color: "white",
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              background: "rgba(255, 255, 255, 0.2)",
              borderRadius: "50%",
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AddIcon sx={{ fontSize: 18, color: "white" }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 16,
              color: "white",
            }}
          >
            {editMode ? "Edit Calculated Column" : "Add Calculated Column"}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: "white",
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content - Scrollable */}
      <Box
        sx={{
          p: 3,
          overflowY: "auto",
          overflowX: "hidden",
          flex: 1,
          minHeight: 0,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#cbd5e1",
            borderRadius: "4px",
            "&:hover": {
              background: "#94a3b8",
            },
          },
        }}
      >
        {/* Table Selection Dropdown (only show if isPostgres is set) */}
        {(isPostgres !== undefined) && (
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "#64748b",
                mb: 1,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Select Table
            </Typography>
            <Dropdown
              options={tableOptions}
              placeholder="Select a table..."
              value={selectedTable}
              onChange={handleTableChange}
              multiple={false}
              searchable={true}
              variant="default"
              disabled={true}
            />
          </Box>
        )}

        {/* Available Columns Helper */}
        {displayColumns.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "#64748b",
                mb: 1,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Available Columns {selectedTable ? `(${cleanTableName(selectedTable)})` : ""}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                p: 1.5,
                bgcolor: "#f8fafc",
                borderRadius: 1.5,
                border: "1px solid #e2e8f0",
              }}
            >
              {displayColumns
                .filter((col) => {
                  // Filter out null/undefined and columns without names
                  if (!col) return false;
                  const colName = typeof col === "string" ? col : (col.name || "");
                  return !!colName;
                })
                .map((col) => {
                // Handle both string (backward compatibility) and object format
                const colName = typeof col === "string" ? col : (col.name || "");
                
                const colType = typeof col === "object" ? col.type : null;
                const sanitizedCol = colName.replace(/\s+/g, "_");
                
                // Get type display name
                const getTypeDisplay = (type) => {
                  if (!type) return "";
                  const typeLower = type.toLowerCase();
                  if (typeLower.includes("int") || typeLower === "integer" || typeLower === "number") return "int";
                  if (typeLower.includes("float") || typeLower.includes("double") || typeLower.includes("decimal") || typeLower.includes("numeric")) return "float";
                  if (typeLower.includes("bool")) return "bool";
                  if (typeLower.includes("date") || typeLower.includes("timestamp")) return "date";
                  return "text";
                };
                
                const typeDisplay = getTypeDisplay(colType);
                
                return (
                  <Button
                    key={colName}
                    size="small"
                    variant="outlined"
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      try {
                        e?.preventDefault?.();
                        e?.stopPropagation?.();
                        handleInsertColumn(col);
                      } catch (error) {
                        console.error('Error inserting column:', error);
                      }
                    }}
                    sx={{
                      fontSize: 11,
                      textTransform: "none",
                      borderColor: "#cbd5e1",
                      color: "#475569",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 0.25,
                      py: 0.75,
                      px: 1,
                      "&:hover": {
                        borderColor: "#3F49A7",
                        color: "#3F49A7",
                        bgcolor: "rgba(63, 73, 167, 0.05)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 500 }}>
                        {sanitizedCol}
                      </Typography>
                      {typeDisplay && (
                        <Chip
                          label={typeDisplay}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: 9,
                            fontWeight: 600,
                            bgcolor: typeDisplay === "int" || typeDisplay === "float" 
                              ? "#e0f2fe" 
                              : typeDisplay === "bool"
                              ? "#fef3c7"
                              : "#f3f4f6",
                            color: typeDisplay === "int" || typeDisplay === "float"
                              ? "#0369a1"
                              : typeDisplay === "bool"
                              ? "#92400e"
                              : "#6b7280",
                            border: "none",
                            "& .MuiChip-label": {
                              px: 0.75,
                              py: 0,
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Button>
                );
              })}
            </Box>
            <Typography
              sx={{
                fontSize: 11,
                color: "#64748b",
                mt: 1,
                fontStyle: "italic",
              }}
            >
              Click on a column name to insert it into the formula
            </Typography>
          </Box>
        )}

        {/* Column Name Input */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "#374151",
              mb: 1,
            }}
          >
            Column Name *
          </Typography>
          <TextField
            placeholder="e.g. Total_Amount"
            value={columnName}
            onChange={(e) => {
              const value = e.target.value;
              setColumnName(value);
              
              // Real-time validation
              if (value.trim()) {
                const columnNameRegex = /^[a-zA-Z0-9_ ]+$/;
                if (!columnNameRegex.test(value)) {
                  setError("Special characters are not allowed in column name");
                } else if (!/^[a-zA-Z_]/.test(value.trim())) {
                  setError("Column name must start with a letter or underscore");
                } else {
                  // Check for duplicate computed column names
                  const computedColumnsToCheck = editMode 
                    ? (existingComputedColumns || []).filter(cc => cc.columnName !== editColumnName)
                    : (existingComputedColumns || []);
                  const computedColumnNames = computedColumnsToCheck.map(cc => cc.columnName);
                  
                  if (computedColumnNames.includes(value.trim())) {
                    setError(`A calculated column named "${value.trim()}" already exists`);
                  } else {
                    setError("");
                  }
                }
              } else {
                setError("");
              }
            }}
            fullWidth
            autoFocus
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                fontSize: 13,
                "&:focus-within": {
                  borderColor: "#3F49A7",
                },
              },
            }}
            helperText="Only letters, numbers, underscores, and spaces allowed"
          />
        </Box>

        {/* Formula Input */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#374151",
              }}
            >
              Formula *
            </Typography>
            <Button
              size="small"
              startIcon={<FunctionsIcon sx={{ fontSize: 14 }} />}
              onClick={() => setShowFunctions(!showFunctions)}
              sx={{
                fontSize: 11,
                textTransform: "none",
                color: "#3F49A7",
                "&:hover": {
                  bgcolor: "rgba(63, 73, 167, 0.05)",
                },
              }}
            >
              {showFunctions ? "Hide" : "Show"} Functions
            </Button>
          </Box>
          <TextField
            placeholder="e.g. =SUM(Column1, Column2)"
            value={formula}
            inputRef={formulaInputRef}
            onChange={(e) => {
              const newFormulaValue = e.target.value;
              setFormula(newFormulaValue);

              // Real-time validation for formula
              if (newFormulaValue.trim()) {
                const validationResult = validateBasicFormulaSyntax(newFormulaValue);
                if (!validationResult.isValid) {
                  setError(validationResult.errorMessage);
                } else {
                  setError("");
                }
              } else {
                setError("");
              }
            }}
            onClick={handleCursorPositionChange}
            onKeyUp={handleCursorPositionChange}
            onSelect={handleCursorPositionChange}
            fullWidth
            multiline
            rows={3}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: "#64748b",
                      fontFamily: "monospace",
                    }}
                  >
                    
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                fontSize: 13,
                fontFamily: "monospace",
                "&:focus-within": {
                  borderColor: "#3F49A7",
                },
              },
            }}
          />
          <Typography
            sx={{
              fontSize: 11,
              color: "#64748b",
              mt: 1,
            }}
          >
            Use Excel-like formulas. Column names should use underscores instead
            of spaces (e.g. Product_Price). Click "Show Functions" to browse available formulas.
          </Typography>

          {/* Functions Browser */}
          {showFunctions && (
            <Box
              sx={{
                mt: 2,
                border: "1px solid #e2e8f0",
                borderRadius: 1.5,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Search Bar */}
              <Box sx={{ p: 1.5, borderBottom: "1px solid #e2e8f0" }}>
                <Paper
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 1.5,
                    py: 0.5,
                    bgcolor: "#f8fafc",
                  }}
                >
                  <SearchIcon sx={{ color: "#64748b", fontSize: 18, mr: 1 }} />
                  <InputBase
                    placeholder="Search functions..."
                    value={functionSearch}
                    onChange={(e) => setFunctionSearch(e.target.value)}
                    sx={{ flex: 1, fontSize: 12 }}
                  />
                </Paper>
              </Box>

              {/* Category Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={selectedCategory}
                  onChange={(e, newValue) => setSelectedCategory(newValue)}
                  variant="fullWidth"
                  sx={{
                    minHeight: 36,
                    "& .MuiTab-root": {
                      fontSize: 11,
                      textTransform: "none",
                      minHeight: 36,
                      py: 0.5,
                    },
                  }}
                >
                  {categoryNames.map((cat) => (
                    <Tab key={cat} label={cat} />
                  ))}
                </Tabs>
              </Box>

              {/* Functions List */}
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "#f8fafc",
                  minHeight: 200,
                  maxHeight: 300,
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#cbd5e1",
                    borderRadius: "4px",
                    "&:hover": {
                      background: "#94a3b8",
                    },
                  },
                }}
              >
                {functionSearch ? (
                  // Show search results
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#64748b",
                        mb: 1,
                      }}
                    >
                      Search Results ({getFilteredFunctions().length})
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {getFilteredFunctions().map((funcName) => {
                        const syntax = getFunctionSyntax(funcName);
                        return (
                          <Chip
                            key={funcName}
                            label={funcName}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleInsertFunction(funcName)}
                            size="small"
                            sx={{
                              fontSize: 10,
                              height: 24,
                              cursor: "pointer",
                              "&:hover": {
                                bgcolor: "#3F49A7",
                                color: "white",
                              },
                            }}
                            title={syntax}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                ) : (
                  // Show category functions
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#64748b",
                        mb: 1,
                      }}
                    >
                      {categoryNames[selectedCategory]} Functions
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {BASIC_FORMULAS[categoryNames[selectedCategory]]?.map((funcName) => {
                        const syntax = getFunctionSyntax(funcName);
                        return (
                          <Chip
                            key={funcName}
                            label={funcName}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleInsertFunction(funcName)}
                            size="small"
                            sx={{
                              fontSize: 10,
                              height: 24,
                              cursor: "pointer",
                              "&:hover": {
                                bgcolor: "#3F49A7",
                                color: "white",
                              },
                            }}
                            title={syntax}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Error Message */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              fontSize: 12,
              "& .MuiAlert-message": {
                fontSize: 12,
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mt: 2,
          }}
        >
          <Button
            onClick={() => {
              if (sqlError && onDeleteColumn && editColumnName) {
                // If there's an SQL error, this is a "Delete Column" button
                onDeleteColumn(editColumnName);
              } else {
                // Otherwise, just close the modal
                onClose();
              }
            }}
            variant="outlined"
            size="small"
            sx={{
              borderColor: sqlError ? "#ef4444" : "#d1d5db",
              color: sqlError ? "#ef4444" : "#6b7280",
              fontWeight: 500,
              fontSize: 13,
              borderRadius: 1.5,
              px: 2,
              py: 0.8,
              textTransform: "none",
              minWidth: 70,
              "&:hover": {
                borderColor: sqlError ? "#dc2626" : "#9ca3af",
                backgroundColor: sqlError ? "#fef2f2" : "#f9fafb",
              },
            }}
          >
            {sqlError ? "Delete Column" : "Cancel"}
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!columnName.trim() || !formula.trim()}
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            sx={{
              background: "linear-gradient(135deg, #3F49A7 0%, #2c357a 100%)",
              color: "white",
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 1.5,
              px: 2,
              py: 0.8,
              textTransform: "none",
              minWidth: 90,
              boxShadow: "0 2px 6px rgba(63, 73, 167, 0.2)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, #2c357a 0%, #1e2a4a 100%)",
                boxShadow: "0 3px 8px rgba(63, 73, 167, 0.3)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                background: "#f3f4f6",
                color: "#9ca3af",
                boxShadow: "none",
                transform: "none",
              },
              transition: "all 0.2s ease",
            }}
          >
            {editMode ? "Update Column" : "Add Column"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AddColumnModal;

